import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import {memo, Suspense} from 'react'
import type {EntryPointProps} from 'react-relay'
import type {PreloadedQuery} from 'react-relay/relay-hooks/EntryPointTypes'

import type {BuildId} from '../../../../../../../types'
import type {CommiterSelectQuery} from '../../../../../../common/UserSelect/__generated__/CommiterSelectQuery.graphql'
import {ChangeContext} from '../../../../../../packages/Changes/Change/Change.context'
import ChangesLog from '../../../../../../packages/Changes/ChangesLog/ChangesLog'

import ChangesFilter from './ChangesFilter/ChangesFilter'
import useChangesTab from './ChangesTab.hooks'

import styles from './ChangesTab.css'

const loader = (
  <div className={styles.wrapper} data-test-changes-advanced-view>
    <div className={styles.content}>
      <LoaderInline>{'Loading...'}</LoaderInline>
    </div>
  </div>
)

type OwnProps = {
  className?: string
  jobId: BuildId
}

type Props = OwnProps & {
  commiterSelectQueryRef: PreloadedQuery<CommiterSelectQuery>
}

function ChangesTab({jobId, commiterSelectQueryRef}: Props) {
  const {headId, locator, queued, changesListContextValue, hasChangeLog, isChangeLogReady} =
    useChangesTab(jobId)

  if (locator == null) {
    return null
  }

  if (!isChangeLogReady) {
    return loader
  }

  return hasChangeLog ? (
    <div className={styles.wrapper} data-test-changes-advanced-view>
      <div className={styles.filter}>
        <Suspense>
          <ChangesFilter
            className={styles.changesFilter}
            commiterSelectQueryRef={commiterSelectQueryRef}
            buildTypeId={headId}
          />
        </Suspense>
      </div>
      <div className={styles.content}>
        <ChangeContext.Provider value={changesListContextValue}>
          <Suspense>
            <ChangesLog buildTypeId={headId} locator={locator} />
          </Suspense>
        </ChangeContext.Provider>
      </div>
    </div>
  ) : (
    <div data-test-changes-advanced-view className={styles.emptyList}>
      {`There are no changes found${queued ? ' or they are not yet collected' : ''}`}
    </div>
  )
}

type Queries = {commiterSelect: CommiterSelectQuery}

function ChangesTabContainer({queries, props}: EntryPointProps<Queries, {}, OwnProps, {}>) {
  return (
    <Suspense fallback={loader}>
      <ChangesTab {...props} commiterSelectQueryRef={queries.commiterSelect} />
    </Suspense>
  )
}

export type ChangesTabComponent = typeof ChangesTabContainer

export default memo(ChangesTabContainer)
